/**
 * Canonical formatting for the `product_highlight(s)` field, which arrives in
 * several wire shapes across the app (an array from the curation product detail,
 * a `values` array or a pipe-delimited scalar from the enriched-products
 * endpoints). All highlight formatting should go through here so every surface
 * renders the same list the same way.
 */

/** Item separator for the canonical highlights string. Matches the bullets
 *  renderer (`EnrichmentOptContent` splits on `|`) and is comma-safe — a single
 *  highlight can itself contain a comma (e.g. "Open-toe, slip-on construction"),
 *  so a comma join would be lossy. */
const HIGHLIGHTS_ITEM_SEPARATOR = ' | ';

/**
 * Normalises a productHighlights value from any wire shape into its individual
 * highlight items (trimmed, blanks dropped):
 *  - `string[]`     → the items themselves (lossless).
 *  - pipe string    → split on `|` (already the canonical delimiter).
 *  - other string   → a single item (never comma-split — an item may contain a comma).
 *  - null/undefined → `[]`.
 */
export function toProductHighlightItems(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  const parts = Array.isArray(value) ? value : value.includes('|') ? value.split('|') : [value];
  return parts.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean);
}

/**
 * Formats a productHighlights value into the canonical pipe-delimited string the
 * bullets renderer expects. Pass any wire shape; get back one string ready for
 * `EnrichmentOptContent type="bullets"` (empty string when there are no items).
 */
export function formatProductHighlights(value: string | string[] | null | undefined): string {
  return toProductHighlightItems(value).join(HIGHLIGHTS_ITEM_SEPARATOR);
}

/**
 * Formats a productHighlights value the way the GMC feed export encodes it
 * (the "Quoted list" rule): each item double-quoted, joined with a bare comma
 * — e.g. `"Item one","Item two"`. Pass any wire shape; get back one string
 * ready to render as plain text (empty string when there are no items).
 */
export function formatProductHighlightsForFeed(
  value: string | string[] | null | undefined,
): string {
  return toProductHighlightItems(value)
    .map((item) => `"${item}"`)
    .join(',');
}
